import { Inject, Injectable } from "@angular/core";
import { Environment } from "../classes/environment";
import { HttpClient } from "@angular/common/http";
import { catchError, map, Observable, of } from "rxjs";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { TenantContextService } from "./tenantcontext.service";
import { extractTenantFromLocation } from "../core/tenant.util";


@Injectable( { providedIn: "root" } )
export class CustomerService {


	private logoSafeUrl: SafeUrl = "";
	private readonly defaultFavicon = "assets/favicon.ico";


	constructor(
		@Inject('environment') private environment: Environment,
		private httpClient: HttpClient,
		private domSanitizer: DomSanitizer,
		private tenantContextService: TenantContextService
	) {
	}


	loadLegalInformation(): Observable<{ contact: string, imprint: string, privacyPolicy: string, privacyPolicyChat: string, termsOfUse: string, termsOfUseChat: string }|null> {

		const cachedRaw = window.localStorage.getItem("kundenCache");
		if (cachedRaw) {
			try {
				const cached = JSON.parse(cachedRaw);
				const hasLegalFields = cached && (
					Object.prototype.hasOwnProperty.call(cached, "kundenKontakt") ||
					Object.prototype.hasOwnProperty.call(cached, "kundenImpressum") ||
					Object.prototype.hasOwnProperty.call(cached, "kundenDatenschutz") ||
					Object.prototype.hasOwnProperty.call(cached, "kundenDatenschutzChat") ||
					Object.prototype.hasOwnProperty.call(cached, "kundenNutzungsbedingungen") ||
					Object.prototype.hasOwnProperty.call(cached, "kundenNutzungsbedingungenChat")
				);
				if (hasLegalFields) {
					return of({
						contact: cached.kundenKontakt || "",
						imprint: cached.kundenImpressum || "",
						privacyPolicy: cached.kundenDatenschutz || "",
						privacyPolicyChat: cached.kundenDatenschutzChat || "",
						termsOfUse: cached.kundenNutzungsbedingungen || "",
						termsOfUseChat: cached.kundenNutzungsbedingungenChat || ""
					});
				}
			} catch {
				// Ignore cache parse errors and fall back to HTTP.
			}
		}

		const tenant = extractTenantFromLocation()
			|| this.tenantContextService.getTenant()()
			|| window.localStorage.getItem("tenantContext")
			|| "";
		if (!tenant) {
			console.error("Tenant missing while loading legal information");
			return of(null);
		}

		return this.httpClient.get<{
			kundenDatenschutz: string|null,
			kundenDatenschutzChat: string|null,
			kundenImpressum: string|null, 
			kundenKontakt: string|null,
			kundenNutzungsbedingungen: string|null,
			kundenNutzungsbedingungenChat: string|null,
		}>(
			this.environment.backendUrl + "kunde/" + tenant
		).pipe(
			map( result => {
				if( !result ) return null;
				return {
					contact: result.kundenKontakt || "",
					imprint: result.kundenImpressum || "",
					privacyPolicy: result.kundenDatenschutz || "",
					privacyPolicyChat: result.kundenDatenschutzChat || "",
					termsOfUse: result.kundenNutzungsbedingungen || "",
					termsOfUseChat: result.kundenNutzungsbedingungenChat || ""
				}
			}),
			catchError( err => {
				console.error( "an error happened while loading the legal documents", err );
				return of(null);
			})
		);

	}

	saveLegalInformation( legalType: string, html: string ): Observable<boolean> {

		const tenant = extractTenantFromLocation()
			|| this.tenantContextService.getTenant()()
			|| window.localStorage.getItem("tenantContext")
			|| "";
		if (!tenant) {
			console.error("Tenant missing while saving legal information", legalType);
			return of(false);
		}

		return this.httpClient.put<{ verarbeitet: boolean }>(
			this.environment.backendUrl + "kunde/" + tenant + "/write/" + legalType,
			html
		).pipe(
			map( result => {
				return result && result.verarbeitet;
			}),
			catchError( err => {
				console.error( "an error happened while saving the legal information", legalType, err );
				return of(false);
			})
		);

	}

	loadLogo(): Observable<boolean> {
			
		this.logoSafeUrl = "";
		this.setFavicon();

		const cachedRaw = window.localStorage.getItem("kundenCache");
		if (cachedRaw) {
			try {
				const cached = JSON.parse(cachedRaw);
				const hasLogo = cached && (
					Object.prototype.hasOwnProperty.call(cached, "kundenLogoDatei")
					|| Object.prototype.hasOwnProperty.call(cached, "kundenFaviconDatei")
				);
				if (hasLogo) {
					if (cached.kundenFaviconDatei) {
						this.setFavicon("data:image/x-icon;base64," + cached.kundenFaviconDatei);
					}

					if (!cached.kundenLogoDatei) return of(false);
					const objectURL = "data:image/jpeg;base64," + cached.kundenLogoDatei;
					this.logoSafeUrl = this.domSanitizer.bypassSecurityTrustUrl(objectURL);
					return of(true);
				}
			} catch {
				// Ignore cache parse errors and fall back to HTTP.
			}
		}

		const tenant = extractTenantFromLocation()
			|| this.tenantContextService.getTenant()()
			|| window.localStorage.getItem("tenantContext")
			|| "";
		if (!tenant) {
			console.error("Tenant missing while loading logo");
			return of(false);
		}

		return this.httpClient.get<{
			kundenLogoDatei: string|null,
			kundenFaviconDatei?: string|null
		}>(
			this.environment.backendUrl + "kunde/" + tenant
		).pipe(
			map( result => {
				if( result && result.kundenFaviconDatei ) {
					this.setFavicon( 'data:image/x-icon;base64,' + result.kundenFaviconDatei );
				}

				if( !result || !result.kundenLogoDatei ) return false;
				const objectURL = 'data:image/jpeg;base64,' + result.kundenLogoDatei;
				this.logoSafeUrl = this.domSanitizer.bypassSecurityTrustUrl( objectURL );
				return true;
			}),
			catchError( err => {
				console.error( "an error happened while loading the logo", err );
				return of(false);
			})
		);

	}

	getLoadedLogo(): SafeUrl {
		return this.logoSafeUrl;
	}

	private setFavicon( href: string = this.defaultFavicon ) {
		const head = document.head;
		if( !head ) return;

		let linkElement = head.querySelector<HTMLLinkElement>("link[rel*='icon']");
		if( !linkElement ) {
			linkElement = document.createElement( "link" );
			linkElement.rel = "icon";
			head.appendChild( linkElement );
		}

		linkElement.href = href;
	}

}
